import React from "react";
import PropTypes from "prop-types";
import { Modal } from "shared/Modal";
import Button, { ButtonPurpose } from "shared/Button";
import { formatCurrency } from "shared/@utils/money";
import { products as productsApi } from "shared/api";
import Loader from "shared/Loader";
import { sortSizes, sizeSortKey } from "BSM/Store/sizeOrder";
import styles from "./AddToCartModal.module.css";

const ONE_SIZE = "One Size Fits All";

// Pick the size a clothing item should default to when the modal opens or the
// color changes. Defaults to Medium when the variation offers it; otherwise
// returns null so the rep is required to pick a size explicitly.
// Matching on the canonical size rank rather than the literal string matters:
// NetSuite spells medium "MD", so a `=== "m"` test silently never fired.
const pickDefaultSize = (sizes) => {
  const list = (sizes || []).filter((s) => s && s !== ONE_SIZE);
  if (!list.length) return null;
  const mediumRank = sizeSortKey("M");
  const medium = list.find((s) => sizeSortKey(s) === mediumRank);
  return medium || null;
};

const realSizes = (sizes) =>
  sortSizes((sizes || []).filter((s) => s && s !== ONE_SIZE));

const variationsOf = (p) => (Array.isArray(p?.variations) ? p.variations : []);

/**
 * Modal for choosing a product's color / size / quantity before it's added to
 * the cart. When `permissionMessage` is set the modal first shows a permission
 * override prompt (the product is restricted to a higher tier); the rep must
 * confirm before the selection step appears.
 *
 * Bundles take a different shape: the bundle itself has no colour/size matrix,
 * its members do. So a bundle asks for one quantity plus a colour/size per
 * member that has variations — you can't ship "a shirt", you ship a specific
 * shirt. The admin's choice from the bundle builder is used as the starting
 * selection; the rep can change it per order.
 */
export function AddToCartModal({
  product,
  isOpen,
  permissionMessage,
  defaults,
  isEdit,
  onClose,
  onConfirm,
}) {
  const variations = React.useMemo(
    () => (Array.isArray(product?.variations) ? product.variations : []),
    [product]
  );
  const colors = variations.map((v) => v.color).filter(Boolean);
  const hasColors = colors.length > 0;
  const isBundle = Boolean(product?.isBundle);

  const [colorIdx, setColorIdx] = React.useState(0);
  const [size, setSize] = React.useState(null);
  const [qty, setQty] = React.useState(1);
  const [overridden, setOverridden] = React.useState(false);
  const [components, setComponents] = React.useState([]);
  const [loadingComponents, setLoadingComponents] = React.useState(false);

  // A bundle only stores its members' *chosen* variation, not their matrices,
  // so fetch each member to know what else it could have been.
  const productId = product?.id;
  React.useEffect(() => {
    if (!isOpen || !isBundle) return undefined;
    const lines = Array.isArray(product?.bundledProducts)
      ? product.bundledProducts
      : [];
    if (!lines.length) {
      setComponents([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingComponents(true);
    Promise.all(
      lines.map((line) =>
        productsApi
          .get(line.productId)
          .then((full) => ({ line, variations: variationsOf(full) }))
          // A member we can't load still belongs in the bundle — it just can't
          // offer alternatives, so fall back to whatever the builder saved.
          .catch(() => ({ line, variations: [] }))
      )
    )
      .then((loaded) => {
        if (cancelled) return;
        setComponents(
          loaded.map(({ line, variations: vars }) => {
            const active =
              vars.find((v) => v.color === line.variationLabel) || vars[0];
            const sizes = realSizes(active?.sizes);
            return {
              productId: line.productId,
              sku: line.sku,
              title: line.title || line.sku,
              variations: vars,
              color: active?.color || line.variationLabel || "",
              size: sizes.includes(line.size) ? line.size : sizes[0] || "",
            };
          })
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingComponents(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isBundle, productId, product]);

  const setComponentColor = (index, variation) => {
    setComponents((prev) =>
      prev.map((c, i) => {
        if (i !== index) return c;
        const sizes = realSizes(variation.sizes);
        return {
          ...c,
          color: variation.color || "",
          size: sizes.includes(c.size) ? c.size : sizes[0] || "",
        };
      })
    );
  };

  const setComponentSize = (index, value) => {
    setComponents((prev) =>
      prev.map((c, i) => (i === index ? { ...c, size: value } : c))
    );
  };

  React.useEffect(() => {
    if (isOpen) {
      const list = Array.isArray(product?.variations) ? product.variations : [];
      // Pre-select the color/size/qty from `defaults` (e.g. re-ordering a
      // line from an existing order) when provided, else fall back to the
      // first variation and the default size.
      let idx = 0;
      if (defaults?.color) {
        const found = list.findIndex((v) => v.color === defaults.color);
        if (found >= 0) idx = found;
      }
      setColorIdx(idx);
      const variation = list[idx];
      setSize(defaults?.size ?? pickDefaultSize(variation?.sizes));
      setQty(defaults?.qty && defaults.qty > 0 ? defaults.qty : 1);
      setOverridden(false);
    }
  }, [isOpen, product?.id, product?.variations, defaults]);

  const activeVariation = hasColors ? variations[colorIdx] : variations[0];
  const sizes = realSizes(activeVariation?.sizes);
  const hasSizes = sizes.length > 0;

  const needsOverride = Boolean(permissionMessage) && !overridden;
  // Every bundle member that offers sizes must have one chosen — an unsized
  // clothing line can't be fulfilled.
  const bundleReady =
    !loadingComponents &&
    components.every(
      (c) =>
        !realSizes(
          (c.variations.find((v) => v.color === c.color) || c.variations[0])
            ?.sizes
        ).length || c.size
    );
  const canAdd = isBundle
    ? bundleReady
    : (!hasColors || activeVariation) && (!hasSizes || size);

  const handleAdd = () => {
    if (isBundle) {
      onConfirm(
        {
          wasOverridden: Boolean(permissionMessage),
          bundleSelections: components.map((c) => ({
            productId: c.productId,
            sku: c.sku,
            color: c.color || undefined,
            size: c.size || undefined,
            variationId: (c.variations.find((v) => v.color === c.color) || {})
              .id,
          })),
        },
        qty
      );
      return;
    }
    onConfirm(
      {
        color: hasColors ? activeVariation?.color : undefined,
        variationId: activeVariation?.id,
        size: hasSizes ? size : undefined,
        wasOverridden: Boolean(permissionMessage),
      },
      qty
    );
  };

  const price = formatCurrency(
    product?.price ?? product?.retailPrice ?? product?.pm
  );

  return (
    <Modal
      title={
        needsOverride
          ? "Product Permissions"
          : isEdit
          ? "Edit"
          : "Add To Cart"
      }
      isOpen={isOpen}
      onClose={onClose}
      className={styles.modal}
    >
      {needsOverride ? (
        <div className={styles.permission}>
          <p className={styles.permissionText}>{permissionMessage}</p>
          <div className={styles.footer}>
            <Button
              type="button"
              purpose={ButtonPurpose.generic}
              title="Cancel and do not override the permission restriction."
              onClick={onClose}
            >
              No
            </Button>
            <Button
              type="button"
              purpose={ButtonPurpose.save}
              title="Override the permission restriction and continue adding this product."
              onClick={() => setOverridden(true)}
            >
              Yes
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.body}>
          <div className={styles.product}>
            {product?.image && (
              <img className={styles.image} src={product.image} alt="" />
            )}
            <div className={styles.productInfo}>
              <p className={styles.name}>{product?.name}</p>
              {price && <p className={styles.price}>{price}</p>}
            </div>
          </div>

          {isBundle && loadingComponents && <Loader />}

          {isBundle && !loadingComponents && components.length === 0 && (
            <p className={styles.bundleEmpty}>
              This bundle has no products in it yet.
            </p>
          )}

          {isBundle &&
            !loadingComponents &&
            components.map((component, index) => {
              const active =
                component.variations.find((v) => v.color === component.color) ||
                component.variations[0];
              const sizes = realSizes(active?.sizes);
              const componentColors = component.variations.filter(
                (v) => v.color
              );
              return (
                <div
                  key={`${component.productId}|${index}`}
                  className={styles.bundleMember}
                >
                  <span className={styles.bundleMemberName}>
                    {component.title}
                    {component.sku ? ` (${component.sku})` : ""}
                  </span>
                  {componentColors.length === 0 && sizes.length === 0 && (
                    <span className={styles.bundleMemberNote}>
                      No options to choose.
                    </span>
                  )}
                  {componentColors.length > 0 && (
                    <div className={styles.section}>
                      <span className={styles.label}>Color</span>
                      <div className={styles.options}>
                        {component.variations.map((v, i) => (
                          <button
                            key={v.id || i}
                            type="button"
                            title={`Use the ${v.color} option for ${component.title}.`}
                            className={`${styles.optionButton}${
                              v.color === component.color
                                ? " " + styles.optionActive
                                : ""
                            }`}
                            onClick={() => setComponentColor(index, v)}
                          >
                            {v.color}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {sizes.length > 0 && (
                    <div className={styles.section}>
                      <span className={styles.label}>Size</span>
                      <div className={styles.options}>
                        {sizes.map((s) => (
                          <button
                            key={s}
                            type="button"
                            title={`Use size ${s} for ${component.title}.`}
                            className={`${styles.optionButton}${
                              s === component.size
                                ? " " + styles.optionActive
                                : ""
                            }`}
                            onClick={() => setComponentSize(index, s)}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

          {!isBundle && hasColors && (
            <div className={styles.section}>
              <span className={styles.label}>Color</span>
              <div className={styles.options}>
                {variations.map((v, i) => (
                  <button
                    key={v.id || i}
                    type="button"
                    title={`Select the ${v.color} color option.`}
                    className={`${styles.optionButton}${
                      i === colorIdx ? " " + styles.optionActive : ""
                    }`}
                    onClick={() => {
                      setColorIdx(i);
                      setSize((prev) => {
                        const nextSizes = (v.sizes || []).filter(
                          (s) => s && s !== ONE_SIZE
                        );
                        // Keep the rep's chosen size if the new color also
                        // offers it; otherwise fall back to the default.
                        if (prev && nextSizes.includes(prev)) return prev;
                        return pickDefaultSize(v.sizes);
                      });
                    }}
                  >
                    {v.color}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isBundle && hasSizes && (
            <div className={styles.section}>
              <span className={styles.label}>Size</span>
              <div className={styles.options}>
                {sizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    title={`Select size ${s}.`}
                    className={`${styles.optionButton}${
                      s === size ? " " + styles.optionActive : ""
                    }`}
                    onClick={() => setSize(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={styles.section}>
            <span className={styles.label}>Quantity</span>
            <div className={styles.qtyStepper}>
              <button
                type="button"
                className={styles.stepButton}
                title="Decrease the quantity."
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                aria-label="Decrease quantity"
              >
                &minus;
              </button>
              <input
                type="number"
                min="1"
                step="1"
                className={styles.qtyInput}
                value={qty}
                onChange={(event) => {
                  const next = parseInt(event.target.value, 10);
                  setQty(Number.isNaN(next) ? 1 : Math.max(1, next));
                }}
                aria-label="Quantity"
              />
              <button
                type="button"
                className={styles.stepButton}
                title="Increase the quantity."
                onClick={() => setQty((q) => q + 1)}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          </div>

          <div className={styles.footer}>
            <Button
              type="button"
              purpose={ButtonPurpose.generic}
              title={
                isEdit
                  ? "Close without changing this line."
                  : "Close without adding this product to the order."
              }
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              purpose={ButtonPurpose.save}
              title={
                isEdit
                  ? "Save these options to the line already in the order."
                  : "Add this product with the selected options to the order."
              }
              onClick={handleAdd}
              disabled={!canAdd}
            >
              {isEdit ? "Update" : "Add To Cart"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

AddToCartModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  product: PropTypes.object,
  isOpen: PropTypes.bool,
  permissionMessage: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  defaults: PropTypes.object,
  /* Re-specing a line already in the order rather than adding a new one. Only
     the wording differs — the selection logic is identical either way. */
  isEdit: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

AddToCartModal.defaultProps = {
  product: null,
  isOpen: false,
  permissionMessage: null,
  defaults: null,
  isEdit: false,
  onClose: () => {},
  onConfirm: () => {},
};

export default AddToCartModal;
